import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { FiDollarSign, FiPlus, FiTrash } from 'react-icons/fi';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import {
  Container,
  Content,
  AddAccountForm,
  AccountList,
  AccountItem,
  NoAccounts,
  SubmitButtonContainer,
  FormArea,
} from './styles';

import Toolbar from '../../components/Toolbar';
import Button from '../../components/Button';
import Input from '../../components/Input';
import LoadingAnimation from '../../components/Loading';
import formatValue from '../../utils/formatValue';
import api from '../../services/api';
import { useToast } from '../../hooks/toast';

interface Account {
  id: string;
  name: string;
  weight: number;
}

interface AccountFromApi {
  id: string;
  name: string;
  type: string;
  weight?: number;
  active: boolean;
}

const AccountsManager: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [sampleIncome, setSampleIncome] = useState(0);

  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);

  useEffect(() => {
    api.get('/account').then((response) => {
      const outcomeAccounts = response.data
        .filter((acc: AccountFromApi) => acc.type === 'OUT')
        .map((acc: AccountFromApi) => ({
          id: acc.id,
          name: acc.name,
          weight: acc.weight ? acc.weight / 100 : 0,
        }));
      setAccounts(outcomeAccounts);
      setLoading(false);
    });
  }, []);

  const handleWeightChange = (id: string, weight: number) => {
    setAccounts((prevAccounts) =>
      prevAccounts.map((acc) => (acc.id === id ? { ...acc, weight } : acc)),
    );
  };

  const totalWeight = useMemo(() => {
    return accounts.reduce((sum, acc) => sum + acc.weight, 0);
  }, [accounts]);

  const handleSubmit = useCallback(async () => {
    if (totalWeight !== 100) {
      addToast({
        type: 'error',
        title: 'Erro na submissão',
        description: `A soma dos pesos deve ser 100%. Atualmente é ${totalWeight}%.`,
      });
      return;
    }

    const payload = accounts.map((acc) => ({
      account_id: acc.id,
      weight: acc.weight * 100,
    }));

    try {
      await api.post('/account-weight', payload);
      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Pesos das contas salvos com sucesso!',
      });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro ao salvar',
        description:
          err.response?.data?.message ||
          'Ocorreu um erro ao salvar os pesos, tente novamente.',
      });
    }
  }, [accounts, totalWeight, addToast]);

  return (
    <Container>
      <Toolbar />
      <Content>
        {loading ? (
          <LoadingAnimation />
        ) : (
          <>
            {accounts.length > 0 ? (
              <FormArea>
                <Form ref={formRef} onSubmit={handleSubmit}>
                  <h2>Registro de Pesos de Contas</h2>
                  <Input
                    name="sampleIncome"
                    icon={FiDollarSign}
                    placeholder="Renda base para cálculo"
                    type="number"
                    onChange={(e) => setSampleIncome(Number(e.target.value))}
                  />
                  <AccountList>
                    {accounts.map((account) => (
                      <AccountItem key={account.id}>
                        <div>
                          <strong>{account.name}</strong>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={account.weight}
                            onChange={(e) =>
                              handleWeightChange(
                                account.id,
                                parseInt(e.target.value, 10),
                              )}
                          />
                          <span>{`${account.weight}%`}</span>
                          <span className="amount">
                            {formatValue((sampleIncome * account.weight) / 100)}
                          </span>
                        </div>
                        {/*
                        <button
                          type="button"
                          onClick={() => handleDeleteAccount(account.id)}
                        >
                          <FiTrash size={20} />
                        </button>
*/}
                      </AccountItem>
                    ))}
                  </AccountList>
                  <Button type="submit" disabled={totalWeight !== 100}>
                    {/* eslint-disable-next-line */}
                    Salvar Pesos ({totalWeight} %)
                  </Button>
                </Form>
              </FormArea>
            ) : (
              <NoAccounts>
                Nenhuma conta de saída encontrada. Adicione uma para começar.
              </NoAccounts>
            )}
          </>
        )}
      </Content>
    </Container>
  );
};

export default AccountsManager;
